import { SelectorScreenMenuType } from '../selector-screen/SelectorScreenMenuType'
import { ConfiguredInterface, ConfiguredProduct } from '../list/ListTypes'
import { ProductDataShape } from '../ProductDataShape'
import React from 'react'
import { throwInline } from '../utils/throwErrorInline'
import {
  findSkinByItemNo,
  getDefaultButtons,
  getTopLevelMenuCategoryForProduct,
  isProductConfigurable,
} from '../products/productUtils'
import { DefaultSwitchSelectionProducts } from '../TopLevelSelectionProducts'
import { ProductOrientation, ProductSeries, SkinProductOption } from '../types/productTypes'
import { DeepLinkData } from './resolveDeeplinkProduct'

export enum ConfiguratorScreenMode {
  ProductSelector = 'ProductSelector',
  ButtonConfiguration = 'ButtonConfiguration',
}

export interface SelectedProductAndOrientation {
  readonly skinItemNo: string
  readonly orientation: ProductOrientation
}

export interface ReducerState {
  expandedMenu: SelectorScreenMenuType
  selectedProductData: SelectedProductAndOrientation
  ledEnabled: boolean
  screen: ConfiguratorScreenMode
  configuredButtonIds: readonly ConfiguredInterface[]
}

export enum ReducerActionType {
  MENU_SELECTED = 'MENU_SELECTED',
  PRODUCT_SELECTED = 'PRODUCT_SELECTED',
  SET_LED_ENABLED = 'SET_LED_ENABLED',
  CONFIGURE_BUTTONS_CLICKED = 'CONFIGURE_BUTTONS_CLICKED',
  SET_CONFIGURED_BUTTONS = 'SET_CONFIGURED_BUTTONS',
  FINISHED_BUTTON_CONFIGURATION = 'FINISHED_BUTTON_CONFIGURATION',
  CHANGE_SERIES = 'CHANGE_SERIES',
  TOGGLE_SHOW_WISER_HUB_PROMPT = 'TOGGLE_SHOW_WISER_HUB_PROMPT'
}

export type ReducerAction = {
  action: ReducerActionType.MENU_SELECTED
  menu: SelectorScreenMenuType
} | {
  action: ReducerActionType.PRODUCT_SELECTED
  products: ProductDataShape
  product: SelectedProductAndOrientation
} | {
  action: ReducerActionType.SET_LED_ENABLED
  enabled: boolean
} | {
  action: ReducerActionType.CONFIGURE_BUTTONS_CLICKED
} | {
  action: ReducerActionType.SET_CONFIGURED_BUTTONS
  configuredButtonIds: readonly ConfiguredInterface[]
} | {
  action: ReducerActionType.FINISHED_BUTTON_CONFIGURATION
  configuredButtonIds: readonly ConfiguredInterface[]
  ledEnabled: boolean
  products: ProductDataShape
} | {
  action: ReducerActionType.CHANGE_SERIES
  series: ProductSeries
  products: ProductDataShape
}

export const ConfiguratorScreenReducer: React.Reducer<Readonly<ReducerState>, ReducerAction> = (state, event): Readonly<ReducerState> => {
  switch (event.action) {
    case ReducerActionType.MENU_SELECTED:
      return {
        ...state,
        expandedMenu: event.menu,
      }
    case ReducerActionType.PRODUCT_SELECTED:
      const nextProduct = event.products.skinGridProducts.find(it =>
        it.itemNo === event.product.skinItemNo
        && it.orientation === event.product.orientation
      ) ?? throwInline(`product not found: "${event.product.skinItemNo}" in ${event.product.orientation}`)

      return {
        ...state,
        selectedProductData: event.product,
        configuredButtonIds: getDefaultButtons(nextProduct, event.products)
      }
    case ReducerActionType.SET_LED_ENABLED:
      return {
        ...state,
        ledEnabled: event.enabled,
      }
    case ReducerActionType.CONFIGURE_BUTTONS_CLICKED:
      return {
        ...state,
        screen: ConfiguratorScreenMode.ButtonConfiguration,
      }
    case ReducerActionType.SET_CONFIGURED_BUTTONS:
      return {
        ...state,
        configuredButtonIds: event.configuredButtonIds,
      }
    case ReducerActionType.FINISHED_BUTTON_CONFIGURATION:
      if (state.screen !== ConfiguratorScreenMode.ButtonConfiguration) {
        return state
      }

      // Reset configuredButtonIds otherwise these button/mech configurations will be kept if the user clicks
      // "Add to my list" on the currently selected product without thus configuring it.
      const currentlyShownProduct = event.products.skinGridProducts.find(it =>
        it.itemNo === state.selectedProductData.skinItemNo
          && it.orientation === state.selectedProductData.orientation
      ) ?? throwInline(`currentlyShownProduct not found: "${state.selectedProductData.skinItemNo}" in ${state.selectedProductData.orientation}`)

      return {
        ...state,
        configuredButtonIds: getDefaultButtons(currentlyShownProduct, event.products),
        screen: ConfiguratorScreenMode.ProductSelector,
      }


    case ReducerActionType.CHANGE_SERIES:
      const isPro = event.series === ProductSeries.Pro

      const selectedProduct = findSkinByItemNo(state.selectedProductData.skinItemNo, event.products.skinGridProducts)

      if (!!selectedProduct.pro === isPro) {
        return state
      }


      const isSelectedTopLevelTypeForSelectedSeries = (productToCompare: SkinProductOption) => {
        return !!productToCompare.pro === isPro
            && getTopLevelMenuCategoryForProduct(productToCompare, SelectorScreenMenuType.TopLevel) === getTopLevelMenuCategoryForProduct(selectedProduct, SelectorScreenMenuType.TopLevel)
      }

      // If the selected product is Outdoor or Wiser,
      //   fall back to Power Points when switching from Iconic to Pro Series
      const isFallbackTopLevelTypeForSelectedSeries = (productToCompare: SkinProductOption) => {
        const topLevelMenuCategoryForProduct = getTopLevelMenuCategoryForProduct(productToCompare, SelectorScreenMenuType.TopLevel)
        const isProductPro = !!productToCompare.pro
        const selectedTopLevelMenuCategory = getTopLevelMenuCategoryForProduct(selectedProduct, SelectorScreenMenuType.TopLevel)

        return isProductPro === isPro
            && ['Outdoor', 'Wiser'].includes(selectedTopLevelMenuCategory)
            && topLevelMenuCategoryForProduct === 'Socket'
      }

      const skins = event.products.skinGridProducts
      const nextSelectedProduct = skins.find(isSelectedTopLevelTypeForSelectedSeries)
        ?? skins.find(isFallbackTopLevelTypeForSelectedSeries)
          ?? skins[0]

      return {
        ...state,
        expandedMenu: SelectorScreenMenuType.TopLevel,
        selectedProductData: {
          skinItemNo: nextSelectedProduct.itemNo,
          orientation: nextSelectedProduct.orientation,
        }
      }
  }
}

export function getReducerStateInitializer(
  Products: ProductDataShape,
  editingProduct: ConfiguredProduct | null,
  deepLinkData: DeepLinkData | null,
  isInEditMode: boolean,
  selectedSeries: ProductSeries
): () => Readonly<ReducerState> {
  return function getInitialReducerState() {
    const editingItem = editingProduct ? { skinItemNo: editingProduct.skinItemNo, orientation: editingProduct.orientation } : null

    const isPro = selectedSeries === ProductSeries.Pro

    // initial selected product:
    //    1. product being edited
    const defaultProduct =
    //    2. product from deep link
      deepLinkData?.skinProductOption
    //    3. default product for series
      ?? Products.skinGridProducts.find(skin => DefaultSwitchSelectionProducts.includes(skin.itemNo) && !!skin.pro === isPro)
    //    3. fallback: any product
      ?? Products.skinGridProducts[0]
      ?? throwInline('no skins found')

    const defaultSkin = editingItem ?? {
      skinItemNo: defaultProduct.itemNo,
      orientation: defaultProduct.orientation,
    }

    const defaultButtonIds: readonly ConfiguredInterface[] = editingProduct?.interfaces ?? []

    // If initial product is a deep link product that comes with included mechs, set up the buttons here.
    const deepLinkButtonIds: ConfiguredInterface[] = []
    if (defaultButtonIds.length === 0 && deepLinkData?.includedMechsItemNo
      && deepLinkData.skinProductOption.gridProduct?.slotCount && deepLinkData.skinProductOption.includedRockerItemNo) {
      for (let i = 0; i < deepLinkData.skinProductOption.gridProduct.slotCount; i++) {
        deepLinkButtonIds.push({
          interfaceItemNo: deepLinkData.skinProductOption.includedRockerItemNo,
          mechItemNo: deepLinkData.includedMechsItemNo
        })
      }
    }

    let screen: ConfiguratorScreenMode

    if (!editingProduct && deepLinkData && isProductConfigurable(deepLinkData.skinProductOption)) {
      screen = ConfiguratorScreenMode.ButtonConfiguration
    } else if (isInEditMode) {
      screen = ConfiguratorScreenMode.ButtonConfiguration
    } else {
      screen = ConfiguratorScreenMode.ProductSelector
    }

    const hasInitialProduct = editingProduct !== null || deepLinkData !== null

    return {
      expandedMenu: hasInitialProduct ? SelectorScreenMenuType.Style : SelectorScreenMenuType.TopLevel,
      selectedProductData: defaultSkin,
      ledEnabled: false,
      configuredButtonIds: deepLinkButtonIds.length > 0 ? deepLinkButtonIds : defaultButtonIds,
      screen,
      hasOptedOutOfWiserHubPrompt: false
    }
  }
}